import { Mail, User as UserIcon, Phone } from 'lucide-react';
import { User } from '../../auth/types';
import { useContext } from 'react';
import { AuthContext } from '../auth/AuthProvider';

interface ProfileTabProps {
  user: User;
}

export function ProfileTab({ user }: ProfileTabProps) {
  // Get the Supabase user to access metadata
  const { user: supabaseUser } = useContext(AuthContext);

  // Check if the name is different from email (meaning we have an actual display name)
  const hasDisplayName = user.name && user.name !== user.email && user.name !== 'User';
  const displayName = hasDisplayName ? user.name : null;
  const avatarText = displayName
    ? displayName.charAt(0).toUpperCase()
    : user.email.charAt(0).toUpperCase();

  // Get phone number from Supabase user metadata
  const phoneNumber =
    supabaseUser?.user_metadata?.phone_number || supabaseUser?.user_metadata?.phone;

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">
          Profile Information
        </h2>
      </div>

      {/* User Avatar */}
      <div className="flex items-center gap-4">
        <div className="w-16 h-16 bg-gradient-to-br from-[#E34290] to-purple-500 rounded-full flex items-center justify-center">
          <span className="text-white text-xl font-medium">{avatarText}</span>
        </div>
        <div>
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
            {displayName || user.email}
          </h3>
          <p className="text-gray-600 dark:text-gray-400">
            Account created and managed via authentication
          </p>
        </div>
      </div>

      {/* Profile Details */}
      <div className="space-y-4">
        {/* Display Name Section (only if available) */}
        {displayName && (
          <div className="flex items-center gap-3 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
            <UserIcon className="h-5 w-5 text-gray-500 dark:text-gray-400" />
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                Display Name
              </label>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{displayName}</p>
            </div>
          </div>
        )}

        {/* Email Section */}
        <div className="flex items-center gap-3 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
          <Mail className="h-5 w-5 text-gray-500 dark:text-gray-400" />
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">
              Email Address
            </label>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{user.email}</p>
          </div>
          <span className="px-2 py-1 text-xs bg-pink-100 dark:bg-pink-900/30 text-pink-800 dark:text-pink-300 rounded-full">
            Verified
          </span>
        </div>

        {/* Phone Number Section (only if available) */}
        {phoneNumber && (
          <div className="flex items-center gap-3 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
            <Phone className="h-5 w-5 text-gray-500 dark:text-gray-400" />
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                Phone Number
              </label>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{phoneNumber}</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
